import { ChangeDetectionStrategy, Component } from '@angular/core';

import { RevealDirective } from '@ledgerly/common/browser';

/**
 * The objection, answered before the feature story rather than after it.
 *
 * Every reader who has used a budgeting app arrives expecting bank import, and
 * the old page left them to work out on their own that there is none. Some of
 * them worked it out on the store listing, some after installing. Both are
 * worse places to find out than here.
 *
 * So this says it plainly and says what you get for it. The absence of a bank
 * connection is the reason the privacy section below can say what it says, and
 * the two only make sense read in that order.
 *
 * The last line sends the wrong reader away on purpose. An install from
 * someone who wanted automatic import is a refund and a one star review, not a
 * conversion.
 */
@Component({
  selector: 'app-no-bank',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RevealDirective],
  template: `
    <section id="no-bank" class="bg-ground pb-[112px]">
      <div class="mx-auto grid max-w-[1180px] gap-16 px-8 lg:grid-cols-2 lg:gap-24">
        <div class="max-w-[46ch]">
          <p
            class="text-kicker uppercase text-accent reveal"
            appReveal
            i18n="@@noBankKicker"
          >
            The trade
          </p>
          <h2
            class="mt-kicker-gap text-headline text-ink text-balance reveal"
            appReveal
            i18n="@@noBankHeadline"
          >
            No bank connection. On purpose.
          </h2>
          <p
            class="mt-8 text-body text-ink-2 reveal"
            appReveal
            i18n="@@noBankBody"
          >
            Ledgerly does not link to your bank, so nothing is imported and
            nothing is categorised for you. You type what you spend, which takes
            a few seconds, and it is the reason nothing has to be uploaded
            unless you switch on backup yourself.
          </p>
          <p
            class="mt-6 text-body text-ink-2 reveal"
            appReveal
            [appReveal]="80"
            i18n="@@noBankFilter"
          >
            If you want automatic import, Ledgerly is not the app for you. If
            you would rather see each amount as you add it, it is.
          </p>
        </div>

        <ul class="space-y-8 lg:pt-[42px]">
          @for (point of points; track point.title) {
          <li class="flex gap-6 reveal" [appReveal]="$index * 80">
            <span
              class="mt-[7px] h-px w-6 flex-none bg-accent-stroke"
              aria-hidden="true"
            ></span>
            <span class="text-label">
              <span class="block text-ink">{{ point.title }}</span>
              <span class="mt-2 block max-w-[42ch] text-ink-2">{{
                point.detail
              }}</span>
            </span>
          </li>
          }
        </ul>
      </div>
    </section>
  `,
})
export class NoBankComponent {
  protected readonly points = [
    {
      title: $localize`:@@noBankPoint1Title:Nothing to connect`,
      detail: $localize`:@@noBankPoint1Detail:No bank login, and no third party holding your credentials on your behalf.`,
    },
    {
      title: $localize`:@@noBankPoint2Title:Nothing to fix later`,
      detail: $localize`:@@noBankPoint2Detail:No guessed categories to go back and correct. You picked them when you added the spend.`,
    },
    {
      title: $localize`:@@noBankPoint3Title:Nothing to wait for`,
      detail: $localize`:@@noBankPoint3Detail:The number moves the moment you add a spend, not the next day when a bank gets round to posting it.`,
    },
  ];
}
